// Portal sign-in. Broker staff have their OWN sign-in (SM-968): a staff
// account on broker-service, unrelated to a trading-site account. Which
// sign-in the portal uses follows the gateway's FF_BROKER_STAFF_IDENTITY,
// read once from GET /api/broker/auth/mode, so cutover and rollback need
// no redeploy:
//
//   staff   /api/broker/auth/{login,login/mfa,me,logout}, plus forgot /
//           set / reset password and the Security page's routes
//   client  the trading-site session (/api/auth/*), as before SM-968
//
//   1. POST login {email, password}
//        200 session            → cookie minted, csrf_token returned
//        428 LoginMFAChallenge  → completeMFA next
//   2. POST login/mfa {challenge_id, factor_id, code} → 200 session
//   GET me                      → AuthMe (401 = no session → null)
//   GET /api/broker/me          → StaffMeResponse
//                                 403 BROKER_SCOPE_DENIED = signed in but
//                                 not broker staff (not a login failure)
//   POST logout
import { APIError, api, setCSRF } from "./api.ts";
import type {
  AuthMe,
  LoginMFAChallenge,
  LoginResponse,
  StaffAccountItem,
  StaffIdentityMode,
  StaffMeResponse,
  StaffSessionResponse,
  StaffTOTPEnrollResponse,
} from "./types.ts";

const STAFF = "/api/broker/auth";

let modePromise: Promise<StaffIdentityMode> | null = null;

/**
 * The deployment's staff identity mode. Anything but a clear "staff"
 * (an older gateway without the route answers 401 or 404) keeps the
 * customer sign-in, today's behaviour.
 */
export function identityMode(): Promise<StaffIdentityMode> {
  if (!modePromise) {
    modePromise = api<{ mode?: string }>(`${STAFF}/mode`, { anonymousOK: true })
      .then((r) => (r?.mode === "staff" ? "staff" : "client"))
      .catch(() => {
        modePromise = null; // ask again next time; a blip must not stick
        return "client" as const;
      });
  }
  return modePromise;
}

/** Test seam: forget the cached mode. */
export function resetIdentityMode(): void {
  modePromise = null;
}

export interface LoginInput {
  email: string;
  password: string;
}

export type LoginResult =
  | { kind: "success"; data: LoginResponse }
  | { kind: "needs_mfa"; challenge: LoginMFAChallenge };

/** A staff session as the shell reads a login (the same fields the customer login answers). */
export function staffLoginResponse(s: StaffSessionResponse): LoginResponse {
  return {
    user_id: s.staff.staff_id,
    email: s.staff.email,
    role: "staff",
    csrf_token: s.csrf_token,
    expires_at: new Date(s.expires_at_unix_ms).toISOString(),
  };
}

function staffAuthMe(a: StaffAccountItem): AuthMe {
  return {
    user_id: a.staff_id,
    email: a.email,
    role: "staff",
    csrf_token: a.csrf_token,
    display_name: a.display_name,
    totp_enrolled: a.totp_enrolled,
  };
}

/** A sign-in step's answer: a session, or a 428 challenge. */
function signInResult(res: LoginResponse | StaffSessionResponse | LoginMFAChallenge, staff: boolean): LoginResult {
  if ((res as LoginMFAChallenge).needs_mfa) {
    return { kind: "needs_mfa", challenge: res as LoginMFAChallenge };
  }
  const ok = staff ? staffLoginResponse(res as StaffSessionResponse) : (res as LoginResponse);
  setCSRF(ok.csrf_token ?? null);
  return { kind: "success", data: ok };
}

export async function login(input: LoginInput): Promise<LoginResult> {
  const staff = (await identityMode()) === "staff";
  const res = await api<LoginResponse | StaffSessionResponse | LoginMFAChallenge>(staff ? `${STAFF}/login` : "/api/auth/login", {
    method: "POST",
    body: input,
    skipCSRF: true,
    expectMFAChallenge: true,
    anonymousOK: true,
  });
  return signInResult(res, staff);
}

export interface CompleteMFAInput {
  challenge_id: string;
  factor_id: string;
  code: string;
}

export async function completeMFA(input: CompleteMFAInput): Promise<LoginResponse> {
  const staff = (await identityMode()) === "staff";
  const res = await api<LoginResponse | StaffSessionResponse>(staff ? `${STAFF}/login/mfa` : "/api/auth/login/mfa", {
    method: "POST",
    body: input,
    skipCSRF: true,
    anonymousOK: true,
  });
  const ok = staff ? staffLoginResponse(res as StaffSessionResponse) : (res as LoginResponse);
  setCSRF(ok.csrf_token ?? null);
  return ok;
}

export async function logout(): Promise<void> {
  try {
    const staff = (await identityMode()) === "staff";
    await api(staff ? `${STAFF}/logout` : "/api/auth/logout", { method: "POST", anonymousOK: true });
  } finally {
    setCSRF(null);
  }
}

/** The session, or null when there is none (401/403). */
export async function fetchMeOrNull(): Promise<AuthMe | null> {
  try {
    const staff = (await identityMode()) === "staff";
    const me = staff
      ? staffAuthMe(await api<StaffAccountItem>(`${STAFF}/me`, { anonymousOK: true }))
      : await api<AuthMe>("/api/auth/me", { anonymousOK: true });
    // /me carries the session's CSRF token too — the only place it is
    // recovered after a reload that lost local storage.
    if (me?.csrf_token) setCSRF(me.csrf_token);
    return me;
  } catch (err) {
    if (err instanceof APIError && (err.status === 401 || err.status === 403)) return null;
    throw err;
  }
}

/** The portal's session bootstrap: every non-removed membership. */
export function fetchStaffMe(): Promise<StaffMeResponse> {
  return api<StaffMeResponse>("/api/broker/me");
}

/** 403 BROKER_SCOPE_DENIED on /api/broker/me: signed in, not broker staff. */
export function isNotStaffError(err: unknown): boolean {
  return err instanceof APIError && err.status === 403 && err.code === "BROKER_SCOPE_DENIED";
}

// --- staff accounts' own sign-in (SM-968) -----------------------------------

/** Always 202: the answer never says whether the email has a staff account. */
export async function forgotPassword(email: string): Promise<void> {
  await api(`${STAFF}/password/forgot`, { method: "POST", body: { email }, skipCSRF: true, anonymousOK: true });
}

/**
 * Redeems an emailed link: "set" (an invite) or "reset" (forgot password).
 * Signs the person in, or — when an authenticator is set up — answers the
 * TOTP challenge the login page's second step completes.
 */
export async function redeemPasswordLink(purpose: "set" | "reset", token: string, password: string): Promise<LoginResult> {
  const res = await api<StaffSessionResponse | LoginMFAChallenge>(`${STAFF}/password/${purpose}`, {
    method: "POST",
    body: { token, password },
    skipCSRF: true,
    expectMFAChallenge: true,
    anonymousOK: true,
  });
  return signInResult(res, true);
}

/** 400 BROKER_STAFF_TOKEN_INVALID: the link expired, was used, or never existed. */
export function isLinkExpired(err: unknown): boolean {
  return err instanceof APIError && err.code === "BROKER_STAFF_TOKEN_INVALID";
}

export function changePassword(currentPassword: string, newPassword: string): Promise<void> {
  return api(`${STAFF}/password/change`, {
    method: "POST",
    body: { current_password: currentPassword, new_password: newPassword },
  });
}

export function enrollTOTP(): Promise<StaffTOTPEnrollResponse> {
  return api<StaffTOTPEnrollResponse>(`${STAFF}/mfa/totp`, { method: "POST", body: {} });
}

export function confirmTOTP(code: string): Promise<StaffAccountItem> {
  return api<StaffAccountItem>(`${STAFF}/mfa/totp/confirm`, { method: "POST", body: { code } });
}

export function removeTOTP(code: string): Promise<StaffAccountItem> {
  return api<StaffAccountItem>(`${STAFF}/mfa/totp/remove`, { method: "POST", body: { code } });
}

export function fetchStaffAccount(): Promise<StaffAccountItem> {
  return api<StaffAccountItem>(`${STAFF}/me`);
}
